// Globe space: the one mapping from geographic lat/lon (degrees) to render-space positions.
// +Y = north pole, lon 0 / lat 0 faces +Z, east is +X.

import { Vector3 } from 'three'

/** Render units; every globe altitude below is a radius in the same units. */
export const GLOBE_RADIUS = 1

/** Radii of the stacked globe layers (ART-DIRECTION §2.4: geography sits under the war network). */
export const GLOBE_ALTITUDE = {
  grid: GLOBE_RADIUS * 1.001,
  landDots: GLOBE_RADIUS * 1.002,
  borders: GLOBE_RADIUS * 1.0025,
  coastline: GLOBE_RADIUS * 1.003,
  network: GLOBE_RADIUS * 1.004,
} as const

export const DEG_TO_RAD = Math.PI / 180

export function latLonToVector3(latDeg: number, lonDeg: number, radius: number, out: Vector3): Vector3 {
  const lat = latDeg * DEG_TO_RAD
  const lon = lonDeg * DEG_TO_RAD
  const cosLat = Math.cos(lat)
  return out.set(radius * cosLat * Math.sin(lon), radius * Math.sin(lat), radius * cosLat * Math.cos(lon))
}

/** Writes the lat/lon (degrees) of a globe-space direction (any length > 0) into `out`; the inverse of latLonToVector3. */
export function directionToLatLon(direction: Vector3, out: { lat: number; lon: number }): void {
  // Clamped: rounding can put |y| / length a hair over 1 at the poles.
  const sinLat = Math.min(1, Math.max(-1, direction.y / direction.length()))
  out.lat = Math.asin(sinLat) / DEG_TO_RAD
  out.lon = Math.atan2(direction.x, direction.z) / DEG_TO_RAD
}

const scratch = new Vector3()

/** Writes the globe-space position of (lat, lon) as xyz into `target` at `offset`. */
export function writeLatLon(target: Float32Array, offset: number, latDeg: number, lonDeg: number, radius: number): void {
  latLonToVector3(latDeg, lonDeg, radius, scratch)
  target[offset] = scratch.x
  target[offset + 1] = scratch.y
  target[offset + 2] = scratch.z
}

/** Strike flight arc apex height per radian of great-circle distance (glyph path and VFX ribbon share it). */
export const STRIKE_ARC_APEX_PER_RAD = 0.25
